import { Dispatch, MiddlewareAPI } from "redux";
import { deleteAccessoryRequest, getAccessoriesRequest, postAccessoryRequest } from "src/store/actions/accessories";
import {
    deleteConditionalRebatesRequest,
    patchConditionalRebatesHandRaisersRequest,
    putConditionalRebatesRequest,
} from "src/store/actions/conditionalRebates";
import { ContinueCreditAppAction, ResetCreditAppAction, SkipCreditApplication } from "src/store/actions/creditApp/base";
import { RemoveScheduleAppointmentAction, UpdateScheduleAppointmentAction } from "src/store/actions/currentDeal";
import { OpenSubModalAction } from "src/store/actions/onlineShopper/subModal";
import { toggleModal } from "src/store/actions/openModal";
import { patchPaymentsRequest } from "src/store/actions/payments";
import { pollingSuccess } from "src/store/actions/poller";
import {
    deleteTradeInRequest,
    getAndPatchTradePendingReportRequest,
    getTradePendingDetailsRequest,
    patchTradeInRequest,
} from "src/store/actions/tradeIn";
import { deleteVehicleSuccess, postVehicleSuccess } from "src/store/actions/vehicles";
import { CONTINUE_CREDIT_APP, RESET_CREDIT_APP, SKIP_CREDIT_APP } from "src/store/constants/creditApp";
import { OPEN_SUB_MODAL } from "src/store/constants/onlineShopper";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { findStep } from "src/store/reducers/creditApp/base";
import { logError } from "src/utils/logger";
import { ApplicationStore } from "Types/state";

const ProductAnalyticsMiddleware = (store: MiddlewareAPI<Dispatch, ApplicationStore>) => (next: Dispatch) => async (
    action:
        | ActionType<typeof patchPaymentsRequest>
        | ActionType<typeof putConditionalRebatesRequest>
        | ActionType<typeof patchConditionalRebatesHandRaisersRequest>
        | ActionType<typeof deleteConditionalRebatesRequest>
        | ActionType<typeof patchTradeInRequest>
        | ActionType<typeof getTradePendingDetailsRequest>
        | ActionType<typeof getAndPatchTradePendingReportRequest>
        | ActionType<typeof deleteTradeInRequest>
        | ActionType<typeof pollingSuccess>
        | ActionType<typeof toggleModal>
        | ActionType<typeof postAccessoryRequest>
        | ActionType<typeof getAccessoriesRequest>
        | ActionType<typeof deleteAccessoryRequest>
        | ActionType<typeof postVehicleSuccess>
        | ActionType<typeof deleteVehicleSuccess>
        | OpenSubModalAction
        | SkipCreditApplication
        | ResetCreditAppAction
        | ContinueCreditAppAction
        | RemoveScheduleAppointmentAction
        | UpdateScheduleAppointmentAction
) => {
    next(action);

    try {
        const state = store.getState();
        const isProd = state.onlineShopper.env === "prod";

        const fireEvent = (eventName: string, parameters: any) => {
            window.osAnalytics("event", eventName, {
                ...parameters,
                ...(!isProd && { traffic_type: "internal" }),
            });
        };

        if (typeof window.osAnalytics === "function") {
            switch (action.type) {
                case patchPaymentsRequest.type:
                    switch (action.meta.analytics.eventName) {
                        case "set_payment_type":
                            fireEvent(action.meta.analytics.eventName, {
                                category: "Payments",
                                payment_type: action.payload.payments.selected,
                            });
                            return;
                        case "set_down_payment":
                            fireEvent(action.meta.analytics.eventName, {
                                category: "Payments",
                                field_type: action.meta.analytics.eventName,
                            });
                            return;
                        default:
                            return;
                    }
                case putConditionalRebatesRequest.type:
                    fireEvent("add_conditional_rebates", {
                        category: "Conditional Rebates",
                    });
                    return;
                case deleteConditionalRebatesRequest.type:
                    fireEvent("remove_conditional_rebates", {
                        category: "Conditional Rebates",
                    });
                    return;
                case patchConditionalRebatesHandRaisersRequest.type:
                    fireEvent("set_hand_raisers", {
                        category: "Conditional Rebates",
                    });
                    return;
                case patchTradeInRequest.type:
                    switch (action.meta.analytics.eventName) {
                        case "start_trade_in":
                            fireEvent(action.meta.analytics.eventName, {
                                category: "Trade In",
                            });
                            return;
                        case "finish_trade_in":
                            fireEvent(action.meta.analytics.eventName, {
                                category: "Trade In",
                                estimateType: action.meta.analytics.eventTradeInType,
                            });
                            return;
                        default:
                            return;
                    }
                case getTradePendingDetailsRequest.type:
                    fireEvent("trade_pending_details", {
                        category: "Trade In",
                    });
                    return;
                case getAndPatchTradePendingReportRequest.type:
                    fireEvent("finish_trade_in", {
                        category: "Trade In",
                        estimateType: action.payload.initiator === "ccdp" ? "CCDP" : "trade_pending",
                    });
                    return;
                case deleteTradeInRequest.type:
                    fireEvent("delete_trade_in", {
                        category: "Trade In",
                    });
                    return;
                case toggleModal.type:
                    if (action.meta.analytics.eventName) {
                        fireEvent(action.meta.analytics.eventName, {
                            category: "Add Vehicle",
                        });
                    }
                    return;
                case OPEN_SUB_MODAL:
                    if (
                        action.data.modalType === "apologiesModal" ||
                        action.data.modalType === "unableToSubmitCreditAppApologyModal" ||
                        action.data.modalType === "unexpectedErrorModal" ||
                        action.data.modalType === "vehicleLimitApologiesModal"
                    ) {
                        fireEvent("exception", {
                            description: action.data.modalType,
                        });
                    }

                    if (action.data.modalType === "badDealApologiesModal") {
                        fireEvent("exception", {
                            description: action.data.modalType,
                            reason: action.data.statusCode,
                        });
                    }
                    return;
                case postVehicleSuccess.type:
                    fireEvent("vehicle_added", {
                        category: "Vehicles",
                        vehicles_total: state.vehicles.order.length,
                    });
                    return;
                case deleteVehicleSuccess.type:
                    if (state.onlineShopper.isAuthed) {
                        fireEvent("vehicle_removed", {
                            category: "Vehicles",
                            vehicles_remaining: state.vehicles.order.length,
                        });
                    }
                    return;
                case pollingSuccess.type:
                    action.payload.deal.vehicles?.forEach((vehicle) => {
                        if (vehicle.payments[vehicle.payments.selected]?.offers?.length === 0) {
                            fireEvent("exception", {
                                description: "Contact Us",
                            });
                        }
                    });
                    return;
                case SKIP_CREDIT_APP:
                    fireEvent("skip_credit_app", {
                        category: "Hard Credit App",
                    });
                    return;
                case RESET_CREDIT_APP:
                    fireEvent("reset_credit_app", {
                        category: "Hard Credit App",
                    });
                    return;
                case CONTINUE_CREDIT_APP:
                    const step = findStep(state.creditApp.base.currentStep);
                    fireEvent("continue_credit_app", {
                        category: "Hard Credit App",
                        step: step?.name,
                    });
                    return;
                case postAccessoryRequest.type:
                    fireEvent("add_accessory", {
                        category: "Accessories",
                    });
                    return;
                case deleteAccessoryRequest.type:
                    fireEvent("delete_accessory", {
                        category: "Accessories",
                    });
                    return;
                case "UPDATE_SCHEDULE_APPOINTMENT":
                    fireEvent("request_appointment", {
                        category: "Request Appointment",
                    });
                    return;
                case "REMOVE_SCHEDULE_APPOINTMENT":
                    fireEvent("cancel_appointment", {
                        category: "Request Appointment",
                    });
                    return;

                default:
                    return;
            }
        }
    } catch (e) {
        logError(e);
    }
};

export default ProductAnalyticsMiddleware;
